import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Link } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import ClientForm from './pages/ClientForm';
import Dashboard from './pages/Dashboard';
import PaymentPage from './pages/PaymentPage';
import JobApplicationPage from './pages/JobApplicationPage';
import ApplicantDashboard from './pages/ApplicantDashboard';
import Application from './pages/Application';
import ClockInOut from './pages/ClockInOut'; // Import ClockInOut page
import InvoicePage from './pages/InvoicePage';
import InvoicePreviewPage from './pages/InvoicePreviewPage';
import InvoiceListPage from './pages/InvoiceListPage';
import BillingCalculator from './pages/BillingCalculator';
import ClientManagementPage from './pages/ClientManagementPage';
import InvoiceEditPage from './pages/InvoiceEditPage';
import EmailPreviewPage from './pages/EmailPreviewPage';
import ThemeToggle from './components/ThemeToggle';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [menuCollapsed, setMenuCollapsed] = useState(false); // State to control menu collapse/expand
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // State for mobile menu

  // Check authentication state on initial load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // First check localStorage
        const authState = localStorage.getItem('isAuthenticated');
        if (authState === 'true') {
          // Verify with server
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/check`, {
            credentials: 'include'
          });
          const data = await response.json();
          
          if (data.isAuthenticated) {
            setIsAuthenticated(true);
            localStorage.setItem('user', JSON.stringify(data.user));
          } else {
            // Server says not authenticated, clean up localStorage
            setIsAuthenticated(false);
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('user');
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        setIsAuthenticated(false);
      } finally {
        setAuthChecked(true);
      }
    };
    
    checkAuth();
  }, []);

  // Handle login and logout
  const login = (user) => {
    setIsAuthenticated(true);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('user', JSON.stringify(user));
  };

  const logout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
  };

  // Toggle side menu collapse
  const toggleMenu = () => {
    setMenuCollapsed(!menuCollapsed);
  };

  // Close mobile menu
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  if (!authChecked) {
    return null; // wait for auth state to be loaded
  }
  return (
    <Router>
      <div className="d-flex">
        {/* Mobile Menu Toggle Button */}
        {isAuthenticated && (
          <button
            className="mobile-menu-toggle btn btn-primary d-md-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              position: 'fixed',
              top: '70px',  // Moved below the header/logo area
              left: '10px',
              zIndex: 1050,
            }}
          >
            <i className={`bi ${mobileMenuOpen ? 'bi-x' : 'bi-list'}`}></i>
          </button>
        )}

        {/* Side Menu */}
        {isAuthenticated && (
          <>
            {/* Mobile menu overlay */}
            {mobileMenuOpen && (
              <div 
                className="mobile-menu-overlay d-md-none"
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  zIndex: 1040,
                }}
              />
            )}
            
            <aside
              className={`border-end sidebar ${menuCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}
              style={{
                width: menuCollapsed ? '80px' : '250px',
                height: '100vh',
                position: 'fixed',
                transition: 'width 0.3s, transform 0.3s',
                zIndex: 1045,
              }}
            >
            <div className="p-3">
              <button
                className="btn btn-outline-secondary w-100 mb-3"
                onClick={toggleMenu}
                aria-label="Toggle menu"
              >
                {menuCollapsed ? '☰' : '←'}
              </button>
              <ul className="list-unstyled">
                <li>
                  <Link className="text-decoration-none d-flex align-items-center" to="/dashboard" onClick={closeMobileMenu}>
                    <i className="bi bi-house me-2"></i>
                    {!menuCollapsed && <span>Dashboard</span>}
                  </Link>
                </li>
                <li>
                  <Link className="text-decoration-none d-flex align-items-center" to="/client-form" onClick={closeMobileMenu}>
                    <i className="bi bi-person me-2"></i>
                    {!menuCollapsed && <span>Client Form</span>}
                  </Link>
                </li>
                <li>
                  <Link className="text-decoration-none d-flex align-items-center" to="/payment" onClick={closeMobileMenu}>
                    <i className="bi bi-credit-card me-2"></i>
                    {!menuCollapsed && <span>Payment</span>}
                  </Link>
                </li>
                <li>
                  <Link className="text-decoration-none d-flex align-items-center" to="/job-application" onClick={closeMobileMenu}>
                    <i className="bi bi-briefcase me-2"></i>
                    {!menuCollapsed && <span>Job Application</span>}
                  </Link>
                </li>
                <li>
                  <Link className="text-decoration-none d-flex align-items-center" to="/application" onClick={closeMobileMenu}>
                    <i className="bi bi-list-task me-2"></i>
                    {!menuCollapsed && <span>View Applications</span>}
                  </Link>
                </li>
                <li>
                  <Link className="text-decoration-none d-flex align-items-center" to="/clock-in-out" onClick={closeMobileMenu}>
                    <i className="bi bi-clock me-2"></i>
                    {!menuCollapsed && <span>Clock In/Out</span>}
                  </Link>
                </li>
                <li>
                  <Link className="text-decoration-none d-flex align-items-center" to="/billing-calculator" onClick={closeMobileMenu}>
                    <i className="bi bi-calculator me-2"></i>
                    {!menuCollapsed && <span>Billing Calculator</span>}
                  </Link>
                </li>
                <li>
                  <Link className="text-decoration-none d-flex align-items-center" to="/invoice" onClick={closeMobileMenu}>
                    <i className="bi bi-receipt me-2"></i>
                    {!menuCollapsed && <span>Generate Invoice</span>}
                  </Link>
                </li>
                <li>
                  <Link className="text-decoration-none d-flex align-items-center" to="/invoice-list" onClick={closeMobileMenu}>
                    <i className="bi bi-file-text me-2"></i>
                    {!menuCollapsed && <span>Invoice History</span>}
                  </Link>
                </li>
                <li>
                  <Link className="text-decoration-none d-flex align-items-center" to="/client-management" onClick={closeMobileMenu}>
                    <i className="bi bi-people me-2"></i>
                    {!menuCollapsed && <span>Client Management</span>}
                  </Link>
                </li>
                <li>
                  <Link className="text-decoration-none d-flex align-items-center" to="/email-preview" onClick={closeMobileMenu}>
                    <i className="bi bi-envelope me-2"></i>
                    {!menuCollapsed && <span>Send Invoice Emails</span>}
                  </Link>
                </li>
                <li>
                  <Link className="text-decoration-none d-flex align-items-center" to="/applicant-dashboard" onClick={closeMobileMenu}>
                    <i className="bi bi-search me-2"></i>
                    {!menuCollapsed && <span>Track Application</span>}
                  </Link>
                </li>
                {!menuCollapsed && (
                  <li className="mt-3">
                    <ThemeToggle variant="sidebar" />
                  </li>
                )}
                {/* Conditionally render logout button */}
                {!menuCollapsed && (
                  <li>
                    <button className="btn btn-outline-danger w-100 mt-3" onClick={logout}>
                      Logout
                    </button>
                  </li>
                )}
              </ul>
            </div>
          </aside>
          </>
        )}

        <div 
          className={`flex-grow-1 main-content ${menuCollapsed ? 'menu-collapsed' : ''} ${!isAuthenticated ? 'no-auth' : ''}`}
        >
          {/* Header */}
          <header className="navbar navbar-expand-lg">
            <div className="container">
              <Link className="navbar-brand" to="/">
                <img
                  src="https://visionkraft-bucket.s3.us-east-1.amazonaws.com/logos/huhc/logoNEW.png"
                  alt="Logo"
                  style={{ height: '40px' }}
                />
              </Link>
              <button
                className="navbar-toggler"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#navbarNav"
                aria-controls="navbarNav"
                aria-expanded="false"
                aria-label="Toggle navigation"
              >
                <span className="navbar-toggler-icon"></span>
              </button>
              <div className="collapse navbar-collapse" id="navbarNav">
                <ul className="navbar-nav ms-auto">
                  <li className="nav-item">
                    <ThemeToggle variant="navbar" showLabel={false} />
                  </li>
                  {isAuthenticated ? (
                    <>
                      <li className="nav-item">
                        <Link className="nav-link" to="/invoice">
                          Invoicing
                        </Link>
                      </li>
                      <li className="nav-item dropdown">
                        <a
                          className="nav-link dropdown-toggle"
                          href="#"
                          id="navbarDropdown"
                          role="button"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          Options
                        </a>
                        <ul className="dropdown-menu" aria-labelledby="navbarDropdown">
                          <li>
                            <Link className="dropdown-item" to="/client-form">
                              Client Form
                            </Link>
                          </li>
                          <li>
                            <Link className="dropdown-item" to="/payment">
                              Payment
                            </Link>
                          </li>
                          <li>
                            <Link className="dropdown-item" to="/job-application">
                              Job Application
                            </Link>
                          </li>
                          <li>
                            <Link className="dropdown-item" to="/application">
                              View Applications
                            </Link>
                          </li>
                          <li>
                            <Link className="dropdown-item" to="/clock-in-out">
                              Clock In/Out
                            </Link>
                          </li>
                          <li>
                            <Link className="dropdown-item" to="/billing-calculator">
                              Billing Calculator
                            </Link>
                          </li>
                          <li>
                            <Link className="dropdown-item" to="/invoice">
                              Generate Invoice
                            </Link>
                          </li>
                          <li>
                            <Link className="dropdown-item" to="/invoice-list">
                              Invoice History
                            </Link>
                          </li>
                          <li>
                            <Link className="dropdown-item" to="/client-management">
                              Client Management
                            </Link>
                          </li>
                          <li>
                            <Link className="dropdown-item" to="/email-preview">
                              Send Invoice Emails
                            </Link>
                          </li>
                          <li>
                            <hr className="dropdown-divider" />
                          </li>
                          <li>
                            <Link className="dropdown-item" to="/applicant-dashboard">
                              <i className="bi bi-search me-2"></i>Track Application
                            </Link>
                          </li>
                        </ul>
                      </li>
                    </>
                  ) : (
                    <>
                      <li className="nav-item">
                        <Link className="nav-link" to="/job-application">
                          Apply Now
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link className="nav-link" to="/applicant-dashboard">
                          Track Application
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link className="btn btn-primary" to="/login">
                          Staff Login
                        </Link>
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </header>

          {/* Main Content with Routes */}
          <main className="container mt-4">
            <Routes>
              {/* Default redirect based on authentication state */}
              <Route
                path="/"
                element={
                  isAuthenticated ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* Login Page */}
              <Route path="/login" element={<LoginPage onLogin={login} />} />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  isAuthenticated ? (
                    <Dashboard />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/client-form"
                element={
                  isAuthenticated ? (
                    <ClientForm />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/payment"
                element={
                  isAuthenticated ? (
                    <PaymentPage />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/application"
                element={
                  isAuthenticated ? (
                    <Application />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/clock-in-out"
                element={
                  isAuthenticated ? (
                    <ClockInOut />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/billing-calculator"
                element={
                  isAuthenticated ? (
                    <BillingCalculator />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/invoice"
                element={
                  isAuthenticated ? (
                    <InvoicePage />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/invoice-preview/:id"
                element={
                  isAuthenticated ? (
                    <InvoicePreviewPage />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/invoice-list"
                element={
                  isAuthenticated ? (
                    <InvoiceListPage />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/client-management"
                element={
                  isAuthenticated ? (
                    <ClientManagementPage />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/invoice/edit/:id"
                element={
                  isAuthenticated ? (
                    <InvoiceEditPage />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/email-preview"
                element={
                  isAuthenticated ? (
                    <EmailPreviewPage />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* Unprotected Routes */}
              <Route path="/job-application" element={<JobApplicationPage />} />
              <Route path="/applicant-dashboard" element={<ApplicantDashboard />} />

              {/* Catch-All Route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </Router>
  );
}

export default App;